import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { HelpHint } from './HelpHint';

/**
 * Incident controls on a ticket — DESIGN.md §12.2.
 *
 * Three situations, and the panel shows exactly one of them:
 *   · an ordinary ticket, which may belong to an open incident
 *   · a ticket already tracked under one
 *   · an incident itself, which can be resolved along with everything under it
 *
 * Suggestions are always *offered*, never applied. A wrong automatic link hides
 * a real second fault behind the first, which is the failure the whole feature
 * exists to prevent.
 */
export function IncidentPanel({
  ticketId,
  isIncident,
  parentIncident,
  childCount,
  status,
}: {
  ticketId: string;
  isIncident: boolean;
  parentIncident: { id: string; reference: string; title: string } | null;
  childCount: number;
  status: string;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
  const fail = (e: unknown) => setError((e as Error).message);

  const { data: suggested } = useQuery({
    queryKey: ['incident-suggestions', ticketId],
    queryFn: () => api.incidentSuggestions(ticketId),
    // Only worth asking when the ticket could actually be linked.
    enabled: !isIncident && !parentIncident && can('ticket:link_incident'),
    staleTime: 60_000,
  });

  const link = useMutation({
    mutationFn: (incidentId: string) => api.linkIncident(ticketId, incidentId),
    onSuccess: refresh,
    onError: fail,
  });

  const unlink = useMutation({
    mutationFn: () => api.unlinkIncident(ticketId),
    onSuccess: refresh,
    onError: fail,
  });

  const mark = useMutation({
    mutationFn: (next: boolean) => api.setIncident(ticketId, next),
    onSuccess: refresh,
    onError: fail,
  });

  const resolve = useMutation({
    mutationFn: () =>
      api.resolveIncident(ticketId, { outcome: 'COMPLETED', note: note || undefined }),
    onSuccess: () => {
      setNote('');
      queryClient.invalidateQueries();
    },
    onError: fail,
  });

  if (!can('ticket:link_incident')) return null;

  const finished = ['WORK_DONE', 'VERIFIED', 'CLOSED', 'CANCELLED'].includes(status);

  return (
    <section className="card">
      <h3 className="mb-3 flex items-center gap-1.5 font-heading text-lg">
        Incident
        <HelpHint>
          One fault can cause many tickets. Link them to a single incident so only one technician is
          sent, then resolve them all together with one message each to the customers.
        </HelpHint>
      </h3>

      {error && (
        <p className="mb-3 rounded-xl bg-danger-50 p-3 text-sm text-danger-700">{error}</p>
      )}

      {/* ── Already tracked under an incident ─────────────────────────── */}
      {parentIncident && (
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Tracked under <span className="font-semibold">{parentIncident.reference}</span>. No
            technician is sent for this ticket on its own.
          </p>
          <button
            type="button"
            className="btn-secondary"
            disabled={unlink.isPending}
            onClick={() => {
              setError(null);
              unlink.mutate();
            }}
          >
            {unlink.isPending ? 'Detaching…' : 'Detach and handle separately'}
          </button>
        </div>
      )}

      {/* ── This ticket is the incident ───────────────────────────────── */}
      {isIncident && (
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {childCount === 0
              ? 'No tickets are linked yet. Link them from the affected customers’ tickets.'
              : `${childCount} ticket${childCount === 1 ? '' : 's'} will be resolved with this one, and each customer told.`}
          </p>

          {!finished && (
            <>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-muted-foreground">
                  What to tell the affected customers (optional)
                </span>
                <input
                  className="input w-full"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  placeholder="Fibre cut on the Kisii road, repaired and spliced."
                />
              </label>
              <button
                type="button"
                className="btn-primary"
                disabled={resolve.isPending}
                onClick={() => {
                  setError(null);
                  resolve.mutate();
                }}
              >
                {resolve.isPending
                  ? 'Resolving…'
                  : childCount > 0
                    ? `Resolve this and ${childCount} linked ticket${childCount === 1 ? '' : 's'}`
                    : 'Resolve this incident'}
              </button>
            </>
          )}

          {resolve.data && (
            <div className="rounded-xl bg-secondary-50 p-3 text-secondary-700">
              <p>{resolve.data.message}</p>
              {resolve.data.skipped.length > 0 && (
                <ul className="mt-2 list-inside list-disc text-danger-700">
                  {resolve.data.skipped.map((s) => (
                    <li key={s.reference}>
                      {s.reference} — {s.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {childCount === 0 && !finished && (
            <button
              type="button"
              className="btn-secondary"
              // The odd one out among this panel's four buttons: the other three
              // were guarded and this was not, so it could be pressed twice.
              disabled={mark.isPending}
              onClick={() => {
                setError(null);
                mark.mutate(false);
              }}
            >
              {mark.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              This is not an incident
            </button>
          )}
        </div>
      )}

      {/* ── An ordinary ticket ────────────────────────────────────────── */}
      {!isIncident && !parentIncident && (
        <div className="space-y-3 text-sm">
          {suggested?.suggestions.length ? (
            <div className="space-y-2">
              <p className="text-muted-foreground">
                This may be part of a fault already being worked on:
              </p>
              {suggested.suggestions.map((s) => (
                <div
                  key={s.incidentId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-3"
                >
                  <div>
                    <p className="font-medium">
                      {s.reference} — {s.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {s.reason} · {Math.round(s.confidence * 100)}% match
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={link.isPending}
                    onClick={() => {
                      setError(null);
                      link.mutate(s.incidentId);
                    }}
                  >
                    Link to this
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">
              No open incident looks related to this ticket.
            </p>
          )}

          {!finished && (
            <button
              type="button"
              className="btn-secondary"
              disabled={mark.isPending}
              onClick={() => {
                setError(null);
                mark.mutate(true);
              }}
            >
              {mark.isPending ? 'Marking…' : 'Make this an incident'}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
